import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useGlobalSearch } from "@/hooks/use-global-search";
import { RecentSearches } from "./RecentSearches";
import { SearchResultGroup } from "./SearchResultGroup";

export function GlobalSearchDesktop() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const search = useGlobalSearch();
  const [location] = useLocation();

  // Close on route change (browser back)
  useEffect(() => {
    if (open) setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // Close on click outside
  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, []);

  // Keyboard shortcut: Ctrl/Cmd+K
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      if (e.key === "Escape" && open) {
        setOpen(false);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  const handleItemClick = useCallback(() => {
    if (search.query.trim()) {
      search.saveRecentSearch(search.query);
    }
    search.clearQuery();
    setOpen(false);
  }, [search]);

  const handleShowAll = useCallback(
    (type: string) => {
      search.setActiveFilter(type);
    },
    [search],
  );

  if (!open) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="gap-2 text-muted-foreground hover:text-foreground"
        onClick={() => {
          setOpen(true);
          setTimeout(() => inputRef.current?.focus(), 50);
        }}
        aria-label={t("globalSearch.openSearch", "Search")}
        data-testid="global-search-trigger"
      >
        <Search className="h-4 w-4" />
        <span className="hidden lg:inline text-sm">
          {t("globalSearch.placeholder", "Search...")}
        </span>
        <kbd className="hidden lg:inline-flex h-5 items-center gap-1 rounded border bg-muted px-1.5 text-[10px] font-medium text-muted-foreground">
          <span className="text-xs">Ctrl</span>K
        </kbd>
      </Button>
    );
  }

  return (
    <div ref={containerRef} className="relative w-72 lg:w-80">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          type="text"
          value={search.query}
          onChange={(e) => {
            search.setQuery(e.target.value);
          }}
          placeholder={t("globalSearch.placeholder", "Search...")}
          className="pl-9 pr-8"
          aria-label={t("globalSearch.placeholder", "Search...")}
          data-testid="global-search-input"
        />
        {search.query && (
          <button
            onClick={() => search.clearQuery()}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label={t("globalSearch.clearInput", "Clear search")}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-popover border rounded-lg shadow-xl max-h-96 overflow-y-auto">
        {!search.isSearching ? (
          <RecentSearches
            searches={search.recentSearches}
            onSelect={(term) => search.setQuery(term)}
            onClear={search.clearRecentSearches}
          />
        ) : search.groupedResults.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground text-center">
            {t("globalSearch.noResults", "No results found")}
          </p>
        ) : (
          search.groupedResults.map((group) => (
            <SearchResultGroup
              key={group.type}
              group={group}
              onItemClick={handleItemClick}
              onShowAll={handleShowAll}
            />
          ))
        )}
      </div>
    </div>
  );
}
